import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { useAppContext } from "../AppContext";
import { API_BASE_URL } from "../common/config.js";
import authHeader from "../services/auth-header.js";
import { MODAL_TYPES } from "../modals/ModalManager";
import { isKnmtPractice, formatStandard, splitTemplateRisk, markOnboardingCompleted, rememberOnboardingLocally } from "./onboarding";
import HowToVideo from "../help/HowToVideo";

// BIV in Dutch, CIA in English.
const ciaKeys = (language) => (language === "nl" ? ["availability", "integrity", "confidentiality"] : ["confidentiality", "integrity", "availability"]);

const WelcomeWizard = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { currentUser, setCurrentUser, currentOrg, welcomeOpen, setWelcomeOpen, openModal, clearAlertMessage } = useAppContext();
  const [step, setStep] = useState(0);
  const [templates, setTemplates] = useState(null);
  const [templateIndex, setTemplateIndex] = useState(0);
  const [selected, setSelected] = useState([]);
  const [hasRisks, setHasRisks] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const dialogRef = useRef(null);

  const eligible = isKnmtPractice(currentUser);
  const alreadyCompleted = !!currentUser?.onboardingCompleted;

  // Open automatically on the first launch. The server is the source of truth, so a
  // tour skipped on another device or before this token was issued stays dismissed.
  useEffect(() => {
    if (!eligible || alreadyCompleted) return undefined;
    let cancelled = false;
    axios.get(`${API_BASE_URL}/api/users/me`, { headers: authHeader() })
      .then(({ data }) => {
        if (cancelled) return;
        if (data?.onboardingCompleted) {
          rememberOnboardingLocally(setCurrentUser);
        } else {
          setWelcomeOpen(true);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [eligible, alreadyCompleted, setCurrentUser, setWelcomeOpen]);

  // Load the same risk templates the risk page offers, plus whether the org already has risks.
  useEffect(() => {
    if (!welcomeOpen || !currentOrg?.id) return undefined;
    let cancelled = false;
    setStep(0);
    setLoadError(false);
    const headers = authHeader();
    Promise.all([
      axios.get(`${API_BASE_URL}/api/riskTemplates/securityStandard/${currentOrg.securityStandard || "ISO27001"}`, { headers }),
      axios.get(`${API_BASE_URL}/api/risks/byOrg/${currentOrg.id}?includeArchived=true`, { headers }),
    ])
      .then(([templateRes, riskRes]) => {
        if (cancelled) return;
        const usable = (templateRes.data || []).filter((tpl) => Array.isArray(tpl.risks) && tpl.risks.length > 0);
        setTemplates(usable);
        setTemplateIndex(0);
        setSelected(usable[0] ? usable[0].risks.map((_, i) => i) : []);
        setHasRisks((riskRes.data || []).length > 0);
      })
      .catch(() => {
        if (cancelled) return;
        setTemplates([]);
        setHasRisks(null);
        setLoadError(true);
      });
    return () => { cancelled = true; };
  }, [welcomeOpen, currentOrg?.id, currentOrg?.securityStandard]);

  const template = templates?.[templateIndex];
  const loaded = templates !== null && (hasRisks !== null || loadError);
  const showStarterSet = loaded && hasRisks === false && !!template;

  const finish = useCallback(() => {
    setWelcomeOpen(false);
    if (!alreadyCompleted) markOnboardingCompleted(setCurrentUser);
  }, [alreadyCompleted, setCurrentUser, setWelcomeOpen]);

  const goTo = (path) => {
    finish();
    navigate(path);
  };

  const startRiskAnalysis = () => {
    const risks = template.risks.filter((_, i) => selected.includes(i));
    finish();
    clearAlertMessage();
    navigate("/risks");
    openModal(MODAL_TYPES.RISK, { riskObj: risks });
  };

  const chooseTemplate = (index) => {
    setTemplateIndex(index);
    setSelected(templates[index].risks.map((_, i) => i));
  };

  const toggleRisk = (index) => {
    setSelected((current) => (current.includes(index) ? current.filter((i) => i !== index) : [...current, index].sort((a, b) => a - b)));
  };

  const allSelected = template && selected.length === template.risks.length;
  const toggleAll = () => setSelected(allSelected ? [] : template.risks.map((_, i) => i));

  useEffect(() => {
    if (!welcomeOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") finish();
    };
    document.addEventListener("keydown", onKeyDown);
    document.body.classList.add("welcome-open");
    dialogRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.classList.remove("welcome-open");
    };
  }, [welcomeOpen, finish]);

  const steps = useMemo(() => [
    t("onboarding.step_welcome"),
    showStarterSet || !loaded ? t("onboarding.step_starter") : t("onboarding.step_ready"),
  ], [t, showStarterSet, loaded]);

  if (!welcomeOpen || !eligible) return null;

  const standard = formatStandard(currentOrg?.securityStandard);

  return createPortal(
    <div className="welcome-overlay" role="presentation">
      <div className="welcome-dialog" role="dialog" aria-modal="true" aria-labelledby="welcome-title" tabIndex={-1} ref={dialogRef}>
        <aside className="welcome-side">
          <i className="icon-risqui welcome-logo" aria-hidden="true" />
          <span className="welcome-badge">Risqui × KNMT</span>
          <ol className="welcome-steps">
            {steps.map((label, i) => (
              <li key={label} className={i === step ? "on" : i < step ? "done" : ""}>
                <span>{i < step ? <i className="icon-check" /> : i + 1}</span>
                {label}
              </li>
            ))}
          </ol>
          <p className="welcome-side-note">
            <i className="icon-idea" aria-hidden="true" />
            {t("onboarding.find_later")}
          </p>
        </aside>

        <div className="welcome-main">
          <button type="button" className="welcome-close" onClick={finish} aria-label={t("onboarding.skip")} title={t("onboarding.skip")}>
            <i className="icon-close" />
          </button>

          {step === 0 && (
            <div className="welcome-pane" key="welcome">
              <h2 id="welcome-title">{t("onboarding.welcome_title", { name: currentOrg?.name })}</h2>
              <p className="welcome-lead">{t("onboarding.welcome_lead", { standard })}</p>
              {showVideo ? (
                <HowToVideo className="welcome-video" autoPlay />
              ) : (
                <ul className="welcome-how">
                  <li>
                    <i className="icon-Risico" aria-hidden="true" />
                    <span><b>{t("onboarding.how_risks_title")}</b><small>{t("onboarding.how_risks_text")}</small></span>
                  </li>
                  <li>
                    <i className="icon-Maatregelen" aria-hidden="true" />
                    <span><b>{t("onboarding.how_measures_title")}</b><small>{t("onboarding.how_measures_text")}</small></span>
                  </li>
                  <li>
                    <i className="icon-evaluation" aria-hidden="true" />
                    <span><b>{t("onboarding.how_evaluate_title")}</b><small>{t("onboarding.how_evaluate_text")}</small></span>
                  </li>
                </ul>
              )}
              <button type="button" className="welcome-video-toggle" onClick={() => setShowVideo((v) => !v)}>
                <i className={showVideo ? "icon-close" : "icon-play"} aria-hidden="true" /> {t(showVideo ? "onboarding.hide_video" : "onboarding.watch_video")}
              </button>
              <div className="welcome-foot">
                <button type="button" className="welcome-link" onClick={finish}>{t("onboarding.skip_look_around")}</button>
                <button type="button" className="welcome-btn primary" onClick={() => setStep(1)}>
                  {t("onboarding.lets_start")} <i className="icon-arrow-right" />
                </button>
              </div>
            </div>
          )}

          {step === 1 && !loaded && (
            <div className="welcome-pane welcome-loading" key="loading">
              <i className="icon-spinner" aria-hidden="true" />
            </div>
          )}

          {step === 1 && showStarterSet && (
            <div className="welcome-pane" key="starter">
              <h2 id="welcome-title">{t("onboarding.starter_title")}</h2>
              <p className="welcome-lead compact">{t("onboarding.starter_lead")}</p>

              <div className="welcome-list-bar">
                {templates.length > 1 ? (
                  <select className="welcome-template-select" value={templateIndex} onChange={(e) => chooseTemplate(Number(e.target.value))} aria-label={t("risk_template")}>
                    {templates.map((tpl, i) => <option key={tpl.id || i} value={i}>{tpl.name} ({tpl.risks.length})</option>)}
                  </select>
                ) : (
                  <span className="welcome-template-name"><i className="icon-Template" aria-hidden="true" /> {template.name}</span>
                )}
                <button type="button" className="welcome-link" onClick={toggleAll}>
                  {allSelected ? t("onboarding.select_none") : t("onboarding.select_all")}
                </button>
              </div>

              <ul className="welcome-risks">
                {template.risks.map((risk, i) => {
                  const { title, body } = splitTemplateRisk(risk.description);
                  const isSelected = selected.includes(i);
                  return (
                    <li key={i}>
                      <label className={isSelected ? "selected" : ""}>
                        <input type="checkbox" checked={isSelected} onChange={() => toggleRisk(i)} />
                        <span className="welcome-check" aria-hidden="true"><i className="icon-check" /></span>
                        <span className="welcome-risk-text">
                          <b>{title}</b>
                          {body && <small>{body}</small>}
                        </span>
                        <span className="welcome-risk-meta">
                          <span className="welcome-cia" aria-label={t("cia")}>
                            {ciaKeys(i18n.language).map((key) => (
                              <span key={key} className={risk[key] ? "on" : ""} title={t(key)}>{t(key).charAt(0)}</span>
                            ))}
                          </span>
                          {risk.measures?.length > 0 && (
                            <span className="welcome-measures">{t("onboarding.measure_count", { count: risk.measures.length })}</span>
                          )}
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>

              <p className="welcome-hint"><i className="icon-info" aria-hidden="true" /> {t("onboarding.starter_hint")}</p>

              <div className="welcome-foot">
                <button type="button" className="welcome-link" onClick={() => setStep(0)}>{t("onboarding.back")}</button>
                <div className="welcome-foot-actions">
                  <button type="button" className="welcome-link" onClick={finish}>{t("onboarding.later")}</button>
                  <button type="button" className="welcome-btn accent" onClick={startRiskAnalysis} disabled={selected.length === 0}>
                    <i className="icon-play" /> {t("onboarding.start_analysis", { count: selected.length })}
                  </button>
                </div>
              </div>
            </div>
          )}

          {step === 1 && loaded && !showStarterSet && (
            <div className="welcome-pane" key="ready">
              <h2 id="welcome-title">{t("onboarding.ready_title")}</h2>
              <p className="welcome-lead">{hasRisks ? t("onboarding.ready_lead_has_risks") : t("onboarding.ready_lead")}</p>
              <div className="welcome-shortcuts">
                <button type="button" onClick={() => goTo("/risks")}>
                  <i className="icon-Risico" aria-hidden="true" />
                  <span><b>{t("risks_plural")}</b><small>{t("onboarding.shortcut_risks")}</small></span>
                  <i className="icon-arrow-right welcome-shortcut-arrow" aria-hidden="true" />
                </button>
                <button type="button" onClick={() => goTo("/help")}>
                  <i className="icon-idea" aria-hidden="true" />
                  <span><b>{t("help.title")}</b><small>{t("onboarding.shortcut_help")}</small></span>
                  <i className="icon-arrow-right welcome-shortcut-arrow" aria-hidden="true" />
                </button>
              </div>
              <div className="welcome-foot">
                <button type="button" className="welcome-link" onClick={() => setStep(0)}>{t("onboarding.back")}</button>
                <button type="button" className="welcome-btn primary" onClick={finish}>
                  {t("onboarding.to_dashboard")} <i className="icon-arrow-right" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default WelcomeWizard;
